import {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect
} from 'react'
import { MenuProvider } from './useMenu'

import { useRouter } from 'next/router'
import { api } from '@/hooks/api'
import { LayoutSystem } from '@/layouts/System'

import { toast } from 'react-toastify'

import { AuthPage } from '@/components/pages/AuthPage'
import { LoadingPage } from '@/components/pages/LoadingPage'
import { setCookie } from '@/utils/cookie'
import { PermissionsEnum, PermissionsList } from '@/@types/permission_list'
import { sortRolesByPrecedence } from '@/utils/userRoles'
import { dropdownOptions } from './menuOptions'

/**
 * Qual segundo fator está em vigor NESTE login.
 *
 * Quem decide é a configuração do assinante (`require_totp` / `require_mfa_token`),
 * que pode ser alterada a qualquer momento. Consequência prática: o valor NÃO é
 * estável entre dois logins do mesmo usuário, nem derivável do estado anterior.
 * Lê-se a cada resposta.
 */
export type MfaMethod = 'totp' | 'email'

/**
 * Corpo do `POST /sessions`, em snake_case como a API devolve.
 *
 * `otpauth_uri` e `secret_manual` só vêm no ramo `mfa_setup_required`; quem já
 * vinculou o app nunca mais recebe o segredo de volta. `timeout_minutes` só vem
 * no modo e-mail, onde o código tem validade própria — o TOTP é gerado pelo
 * app e sua janela não é assunto da API.
 *
 * `method` é opcional no TIPO, não no contrato: a API sempre manda. A tolerância
 * existe para a janela de deploy em que este front rodar contra uma API anterior
 * à chave — ver a normalização em `signIn`.
 */
type SignInResponse =
  | {
      state: 'mfa_setup_required' | 'mfa_token_required'
      challenge: string
      method?: MfaMethod
      otpauth_uri?: string
      secret_manual?: string
      timeout_minutes?: number
    }
  | { state: 'logged_in'; token: string; user: User }

/** Corpo do `POST /sessions-mfa` no caminho feliz — é aqui que a sessão nasce. */
interface MfaResponse {
  token: string
  user: User
  state: 'logged_in'
}

/**
 * O que a `AuthPage` recebe de `signIn`, já em camelCase.
 *
 * Os estados `mfa_*` não têm token: abrem um challenge e quem fecha o login é o
 * `confirmMfa`. `logged_in` (assinante sem segundo fator) já saiu daqui com a
 * sessão gravada, por isso não carrega token nem usuário.
 *
 * `method` entra nos DOIS ramos para que a tela nunca precise adivinhar o texto
 * a partir do estado. No ramo de vinculação ele é `'totp'` por construção: com
 * a chave desligada o `mfa_setup_required` é inalcançável no servidor, porque
 * não se vincula app nenhum enquanto a válvula está aberta.
 */
export type SignInResult =
  | { state: 'logged_in' }
  | {
      state: 'mfa_setup_required'
      challenge: string
      method: 'totp'
      otpauthUri: string
      secretManual: string
    }
  | {
      state: 'mfa_token_required'
      challenge: string
      method: MfaMethod
      /**
       * Validade do código em minutos, só no modo e-mail. `undefined` quando a
       * API não mandou — a tela omite a frase da validade em vez de inventar um
       * número.
       */
      timeoutMinutes?: number
    }

interface AuthState {
  token: string
  user: User
}

interface SignInCredentials {
  email: string
  password: string
  /**
   * Token do reCAPTCHA v3 gerado para ESTE submit. Opcional porque o captcha
   * pode não estar configurado ou o Google pode estar fora do ar; nesses casos
   * o pedido vai sem token e a API decide se exige ou não.
   */
  gresponse?: string
}

interface MfaCredentials {
  challenge: string
  code: string
}

interface AuthContextData {
  token: string
  user: User
  isManager: boolean
  isCoordinator: boolean
  /** Perfis do usuário ("Administrador", "Cadastro"…), do maior para o menor nível de alçada. */
  roles: string[]
  permissions: PermissionAuth[]
  hasPermission: (permission: PermissionsList) => boolean
  revalidatePermission: (permission: PermissionsList) => Promise<boolean>
  /**
   * Primeiro passo: troca e-mail/senha por um challenge. Só persiste sessão
   * quando o assinante não exige segundo fator (`logged_in`); nos demais casos
   * o retorno diz apenas qual tela de segundo fator mostrar.
   */
  signIn(credentials: SignInCredentials): Promise<SignInResult>
  /** Segundo passo: fecha o login quando há segundo fator. */
  confirmMfa(credentials: MfaCredentials): Promise<void>
  signOut(): void
  signOutBecauseOfInactivity(): void
  updateUser(user: User): void
  sendRecovery(email: string): Promise<void>
  recovery(token: string, password: string): Promise<void>
}

const Auth = createContext<AuthContextData>({} as AuthContextData)

const interceptorToastId = 'axios-interceptor'

/**
 * Rotas que autenticam quem AINDA NÃO tem sessão: e-mail/senha em `/sessions`,
 * challenge + código do autenticador em `/sessions-mfa`.
 *
 * Um 401 aqui significa "essa credencial não serve", não "sua sessão caiu" —
 * tratar as duas coisas igual fazia quem errava a senha ver "Você está
 * deslogado, entre novamente". Vale igualmente para o segundo fator: errar o
 * código não é uma sessão expirando, e o signOut ainda apagaria o
 * `@BFCDigital:user` de que a tela de reentrada por inatividade depende.
 */
const CREDENTIAL_ENDPOINTS = ['sessions', 'sessions-mfa']

/**
 * Códigos de recusa de credencial do contrato da API.
 *
 * Os quatro últimos são do segundo fator (`MFA_CODE_REPLAYED` só no modo TOTP:
 * código de e-mail é de uso único e morre com o challenge, nunca volta como
 * replay). Hoje a API os devolve com 400, então caem no ramo genérico de 400 do
 * interceptor e nunca chegam aqui. Estão listados mesmo assim porque é o par
 * obrigatório de `/sessions-mfa` em
 * `CREDENTIAL_ENDPOINTS`: a condição do interceptor é rota E código, e sem o
 * código a rota sozinha não impediria nada. Se o status desses erros um dia
 * virar 401, errar o código do autenticador NÃO pode derrubar a tela de
 * reentrada por inatividade com "Você está deslogado".
 */
const CREDENTIAL_FAILURE_CODES = [
  'INVALID_CREDENTIALS',
  'CAPTCHA_FAILED',
  'MFA_CODE_INVALID',
  'MFA_CODE_REPLAYED',
  'MFA_CHALLENGE_GONE',
  'TOO_MANY_ATTEMPTS'
]

/**
 * Reduz a URL da requisição ao caminho nu, para comparação.
 *
 * Necessário porque o `url` do axios chega em formatos diferentes conforme a
 * chamada: `/sessions`, `sessions` (sem barra, como em `reset-password`) ou a
 * URL absoluta quando alguém passa o host inteiro. Comparar a string crua
 * deixaria passar variações.
 */
function normalizeEndpoint(url?: string): string {
  if (!url) {
    return ''
  }

  return url
    .split('?')[0]
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase()
}

function isCredentialEndpoint(config?: { url?: string }): boolean {
  return CREDENTIAL_ENDPOINTS.includes(normalizeEndpoint(config?.url))
}

export const AuthProvider: React.FC = ({ children }) => {
  const [initialLoading, setInitialLoading] = useState(true)
  const [permissions, setPermissions] = useState<PermissionAuth[]>([])
  const router = useRouter()

  const [data, setData] = useState<AuthState>({} as AuthState)
  const [limitations, setLimitations] = useState({
    isManager: false,
    isCoordinator: false
  })

  const [roles, setRoles] = useState<string[]>([])

  const alternativePermissions = [
    {
      parent: 'Consultar Protesto (Root)',
      options: ['Ver Protesto no Cedente', 'Ver Protesto no Sacado']
    },
    {
      parent: 'Ver Consulta VADU (Root)',
      options: ['Ver Consulta VADU no Cedente', 'Ver Consulta VADU no Sacado']
    },
    {
      parent: 'Ver Serviço de Consulta Serasa (Root)',
      options: [
        'Ver Serviço de Consulta Serasa no Cedente',
        'Ver Serviço de Consulta Serasa no Sacado'
      ]
    }
  ]

  const handlePagePermission = useCallback(async () => {
    if (!api.defaults.headers.Authorization) {
      return
    }

    const { data } = await api.get<
      {
        companyId: number
        permissions: string[]
      }[]
    >('/users/me/permissions')

    const permissions: PermissionAuth[] = []
    data.forEach(item => {
      item.permissions.forEach(permission => {
        permissions.push({
          company_id: item.companyId,
          slug: permission
        })
      })
    })

    const route = router.pathname.split('/')

    if (!route[1]) {
      return true
    }

    let neededPermission = dropdownOptions.find(d => {
      return d.route.includes(`${route[1]}`)
    })?.permission

    // ver a rota

    if (!neededPermission) {
      const subrouter = dropdownOptions
        .filter(d => d.options)
        .map(d => d.options)
        .flat()

      neededPermission = subrouter.find(d => {
        return d.route === router.pathname
      })?.permission

      if (!neededPermission) {
        neededPermission = subrouter.find(d => {
          return d.route.includes(router.pathname)
        })?.permission
      }
    }

    if (!neededPermission) {
      return true
    }

    if (alternativePermissions.find(p => p.parent === neededPermission)) {
      const alternative = alternativePermissions.find(
        p => p.parent === neededPermission
      )
      if (
        permissions.find(p => p.slug === PermissionsEnum[alternative.parent]) ||
        permissions.find(
          p => p.slug === PermissionsEnum[alternative.options[0]]
        ) ||
        permissions.find(
          p => p.slug === PermissionsEnum[alternative.options[1]]
        )
      ) {
        return true
      }
    }

    const permissionItem = PermissionsEnum[neededPermission]
    if (permissions.find(p => p.slug === permissionItem)) {
      return true
    }
    router.push('/')
  }, [router.pathname, api.defaults.headers.Authorization])

  const hasPermission = useCallback(
    (permission: PermissionsList) => {
      if (permissions?.find(p => p.slug === PermissionsEnum[permission])) {
        return true
      }
      return false
    },
    [permissions]
  )

  useEffect(() => {
    if (router.pathname !== '/404' && router.pathname !== '') {
      handlePagePermission()
    }
  }, [router.pathname, api.defaults.headers.Authorization])

  const loadPermissions = useCallback(async () => {
    try {
      const { data } = await api.get<
        {
          companyId: number
          permissions: string[]
        }[]
      >('/users/me/permissions')
      const arr: PermissionAuth[] = []
      data.forEach(item => {
        item.permissions.forEach(permission => {
          arr.push({
            company_id: item.companyId,
            slug: permission
          })
        })
      })

      setPermissions(arr)

      return arr
    } catch (err) {
      return null
    }
  }, [])

  /**
   * Busca as permissões atualizadas no servidor e verifica a permissão
   * informada. Use antes de ações sensíveis, para que uma permissão revogada
   * enquanto a tela estava aberta seja respeitada sem precisar recarregar a
   * página (a lista do contexto também é atualizada, então os componentes
   * que dependem dela são re-renderizados).
   */
  const revalidatePermission = useCallback(
    async (permission: PermissionsList) => {
      const updatedPermissions = await loadPermissions()

      if (!updatedPermissions) {
        // Não foi possível consultar o servidor; mantém o que já estava
        // carregado (a API valida a permissão novamente na requisição).
        return hasPermission(permission)
      }

      return !!updatedPermissions.find(
        p => p.slug === PermissionsEnum[permission]
      )
    },
    [loadPermissions, hasPermission]
  )

  const { t } = router.query

  useEffect(() => {
    if (!t) {
      const token = localStorage.getItem('@BFCDigital:token')
      const user = localStorage.getItem('@BFCDigital:user')

      if (token && user) {
        api.defaults.headers.Authorization = `Bearer ${token}`

        setCookie('token', token, 1)

        setData({ token, user: JSON.parse(user) })
        loadPermissions()
      }
    } else {
      localStorage.removeItem('@BFCDigital:token')
      localStorage.removeItem('@BFCDigital:user')
      setData({} as any)
      router.push(`/?t=${t}`)
    }
    setInitialLoading(false)
  }, [t])

  useEffect(() => {
    let cancelled = false

    async function handle() {
      const { data: dataMe } = await api.get('/me', {
        headers: {
          Authorization: `Bearer ${data.token}`
        }
      })

      // Uma resposta da sessão anterior não pode restaurar dados após logout.
      if (
        cancelled ||
        localStorage.getItem('@BFCDigital:token') !== data.token
      ) {
        return
      }

      setLimitations({
        isManager: typeof dataMe.manager?.id === 'number',
        isCoordinator: typeof dataMe.coordinator?.id === 'number'
      })

      // Os perfis vêm por empresa; o mesmo perfil marcado em duas empresas continua sendo um só
      // para quem lê o cabeçalho.
      setRoles(
        sortRolesByPrecedence(
          (dataMe.companyUser || []).flatMap((companyUser: any) =>
            (companyUser.roleCompanyUser || []).map(
              (item: any) => item?.role?.name
            )
          )
        )
      )

      const user = {
        ...data.user,
        subscriber: dataMe.subscriber,
        manager: dataMe.manager,
        // sem isso o filtro de Gerente do coordenador ia sem `coordinator_id` e listava
        // os gerentes de todos os coordenadores
        coordinator: dataMe.coordinator
      }

      localStorage.setItem('@BFCDigital:user', JSON.stringify(user))
      setData(current =>
        current.token === data.token ? { ...current, user } : current
      )
    }
    if (data.token) {
      handle()
    }

    return () => {
      cancelled = true
    }
  }, [data.token])

  const sendRecovery = useCallback(async (email: string) => {
    await api.post('/forgot-password', {
      email
    })

    toast.success('E-mail de recuperação enviado')

    router.push('/')
  }, [])

  const recovery = useCallback(async (t: string, password: string) => {
    /*
     * Sem parâmetro de tipo: a resposta é descartada. O `AuthResponse` que
     * estava aqui descrevia o corpo `{ token, user }` do login antigo, que esta
     * rota nunca devolveu — redefinir a senha manda o usuário para o login.
     */
    await api.post(
      'reset-password',
      {
        password
      },
      {
        params: {
          token: t
        }
      }
    )

    toast.info('Senha atualizada com sucesso')

    router.push('/')
  }, [])

  const persistSession = useCallback(
    (token: string, user: User) => {
      if (typeof window !== 'undefined') {
        localStorage.setItem('@BFCDigital:token', token)
        localStorage.setItem('@BFCDigital:user', JSON.stringify(user))
      }

      api.defaults.headers.Authorization = `Bearer ${token}`

      setCookie('token', token, 1)

      setData({ token, user })
      loadPermissions()
    },
    [loadPermissions]
  )

  /*
   * SEM try/catch aqui, de propósito.
   *
   * O catch anterior engolia a falha e ainda chamava `toast.error(error.message)`
   * — e como o interceptor rejeita com `error.response` (um AxiosResponse, que
   * não tem `.message`), isso virava um toast vazio empilhado em cima do toast
   * do interceptor. Pior: quem chamava `signIn` recebia uma promessa RESOLVIDA
   * mesmo no erro, então a página não tinha como saber que houve 429 e ler o
   * `retry_after`.
   *
   * Agora o erro sobe para a página, que o usa para estado (contador do 429) e
   * libera o loading no `finally`. A mensagem continua sendo responsabilidade
   * exclusiva do interceptor, para não duplicar.
   */
  const signIn = useCallback(
    async ({
      email,
      password,
      gresponse
    }: SignInCredentials): Promise<SignInResult> => {
      const { data } = await api.post<SignInResponse>('/sessions', {
        email,
        password,
        gresponse
      })

      // Assinante sem segundo fator: a sessão já nasce aqui e o AuthProvider
      // troca a tela sozinho. Token e usuário não saem do hook.
      if (data.state === 'logged_in') {
        persistSession(data.token, data.user)
        return { state: 'logged_in' }
      }

      /*
       * snake_case → camelCase AQUI, e não na página.
       *
       * Não existe transformer global: `src/hooks/api.tsx` é um `axios.create`
       * pelado. Se a conversão ficasse na tela, cada consumidor novo teria de
       * lembrar do formato da API; concentrando aqui, `SignInResult` é a única
       * forma que sai deste hook.
       */
      if (data.state === 'mfa_setup_required') {
        return {
          state: 'mfa_setup_required',
          challenge: data.challenge,
          // Não se lê `data.method` aqui: vinculação de app só existe com a
          // chave ligada, e aceitar um `'email'` neste ramo seria propagar um
          // contrato impossível para dentro da tela do QR.
          method: 'totp',
          otpauthUri: data.otpauth_uri,
          secretManual: data.secret_manual
        }
      }

      /*
       * Normalização do `method` com `'totp'` como padrão.
       *
       * O padrão não é arbitrário: `'totp'` é o texto que a tela já mostrava
       * antes desta mudança, então uma resposta sem `method` (API anterior à
       * chave, durante um deploy) continua coerente. O inverso — assumir
       * `'email'` — faria a tela afirmar que mandou um e-mail que ninguém
       * mandou. Só o valor literal `'email'` liga o modo e-mail.
       *
       * Derivado da resposta ATUAL, sem estado nem cache: a chave pode virar
       * entre dois logins do mesmo usuário.
       */
      const method: MfaMethod = data.method === 'email' ? 'email' : 'totp'

      /*
       * `Number(undefined)` é `NaN`, que não é finito — logo cai em `undefined`
       * sozinho, sem precisar testar a ausência do campo em separado. O `> 0`
       * descarta 0 e negativos, que renderizariam "vale por 0 minutos".
       */
      const timeoutMinutes = Number(data.timeout_minutes)

      return {
        state: 'mfa_token_required',
        challenge: data.challenge,
        method,
        timeoutMinutes:
          Number.isFinite(timeoutMinutes) && timeoutMinutes > 0
            ? timeoutMinutes
            : undefined
      }
    },
    [persistSession]
  )

  /**
   * Segundo passo do login: troca o challenge + código pela sessão.
   *
   * Idêntico nos dois métodos. O código pode ter saído do app autenticador ou
   * de um e-mail, mas o corpo é o mesmo `{ challenge, code }` e o servidor já
   * sabe, pelo challenge, como conferi-lo — o cliente não informa o método e
   * não teria por que ser acreditado se informasse.
   *
   * Com segundo fator, é aqui que token e usuário são persistidos: sem o código
   * não há sessão, e recarregar a página devolve ao login. Sem segundo fator, o
   * `signIn` persiste direto (`logged_in`).
   *
   * Sem try/catch, pelo mesmo motivo do `signIn`: a página precisa do erro para
   * decidir se volta ao passo de credenciais (challenge morto) ou se só mostra
   * as tentativas restantes.
   */
  const confirmMfa = useCallback(
    async ({ challenge, code }: MfaCredentials) => {
      const { data } = await api.post<MfaResponse>('/sessions-mfa', {
        challenge,
        code
      })

      persistSession(data.token, data.user)
    },
    [persistSession]
  )

  const signOut = useCallback(() => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('@BFCDigital:token')
      localStorage.removeItem('@BFCDigital:user')
      localStorage.removeItem('@BFCDigital:oat')
    }

    setData({} as AuthState)
  }, [])

  const signOutBecauseOfInactivity = useCallback(() => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('@BFCDigital:token')
      // localStorage.removeItem('@BFCDigital:user')
      localStorage.removeItem('@BFCDigital:oat')
    }

    setData(data => ({ user: data.user, token: '' } as AuthState))
  }, [])

  useEffect(() => {
    api.interceptors.response.use(
      response => {
        return response
      },
      error => {
        /*
         * Duas condições, não uma. O status sozinho não distingue "senha
         * errada" de "sessão expirada", e o código sozinho não impede que uma
         * rota autenticada qualquer passe a devolver um `code` parecido no
         * futuro e acabe deixando uma sessão morta de pé na tela.
         */
        const isCredentialFailure =
          isCredentialEndpoint(error?.config || error?.response?.config) &&
          CREDENTIAL_FAILURE_CODES.includes(error?.response?.data?.code)

        if (error?.response?.status === 401) {
          if (isCredentialFailure) {
            /*
             * Login recusado: mostra a mensagem genérica que o servidor
             * escolheu e NÃO chama signOut() — não há sessão para encerrar, e
             * o signOut ainda apagaria o `@BFCDigital:user` que a tela de
             * reentrada por inatividade usa para saber de quem é a senha.
             *
             * A mensagem é sempre a do servidor. Ela é uniforme de propósito:
             * o antigo 404 "Email não encontrado" e o 400 "Usuário desativado"
             * revelavam quais e-mails existem na base, então não há nada a
             * detalhar aqui.
             */
            toast.error(
              error?.response?.data?.message ||
                'Não foi possível entrar. Verifique os dados e tente novamente.',
              { toastId: interceptorToastId }
            )
          } else {
            toast.warn('Você está deslogado, entre novamente', {
              toastId: interceptorToastId
            })
            signOut()
          }
        } else if (
          error?.config?.skipErrorToast &&
          error?.response?.status !== 403
        ) {
          // a chamada trata e exibe o próprio erro (o 403 segue abaixo por
          // causa do signOut de acesso restrito)
        } else if (error?.response?.status === 429) {
          /*
           * Excesso de tentativas. A mensagem sai UMA vez (o `toastId` dedupe)
           * e o contador regressivo é problema da tela, que lê o `retry_after`
           * do corpo no próprio catch.
           */
          toast.error(
            error?.response?.data?.message ||
              'Muitas tentativas. Aguarde antes de tentar novamente.',
            { toastId: interceptorToastId }
          )
        } else if (error?.response?.status === 503) {
          /*
           * LOGIN_UNAVAILABLE e afins. Sem este ramo a falha era silenciosa: o
           * botão voltava ao normal e nada aparecia na tela.
           *
           * No passo 1 este status ganhou um significado novo com a válvula do
           * segundo fator: além de "não consegui emitir a sessão", agora também
           * cobre "não consegui ENVIAR o e-mail com o código". Para a tela dá
           * no mesmo — o login não começou e vale repetir —, então continua
           * sendo a mensagem do servidor que manda, sem ramo próprio.
           */
          toast.dark(
            error?.response?.data?.message ||
              'Serviço indisponível no momento, tente novamente em instantes'
          )
        } else if (error?.response?.status === 403) {
          if (
            error?.response?.data?.message ===
            'Acesso restrito no dia ou horário atual.'
          ) {
            signOut()
          }
          toast.error(
            error?.response?.data?.message ||
              'Você não tem permissão à este recurso',
            {
              toastId: interceptorToastId
            }
          )
        } else if (error?.response?.status === 404) {
          if (error?.response?.data?.message) {
            toast.error(error?.response?.data?.message)
          } else {
            toast.error('Recurso não encontrado')
          }
        } else if (
          error?.response?.status === 400 ||
          error?.response?.status === 422
        ) {
          if (error?.response?.data.errors) {
            if (Array.isArray(error?.response.data.errors)) {
              error?.response.data.errors.forEach(d => {
                toast.error(d.message)
              })
            } else {
              toast.error(error?.response.data.errors.message)
            }
          } else {
            if (Array.isArray(error?.response.data)) {
              error?.response.data.forEach(d => {
                toast.error(d.message)
              })
            } else {
              toast.error(error?.response.data.message)
            }
          }
        } else if (error?.response?.status === 500) {
          toast.dark(
            'Ocorreu um erro inesperado em nosso sistema, contate o suporte'
          )
        } else if (error?.response?.status === 502) {
          toast.dark('Sistema fora do ar, contate o suporte')
        }

        return Promise.reject(error.response)
      }
    )
  }, []) // eslint-disable-line

  const updateUser = useCallback(
    (user: User) => {
      localStorage.setItem('@BFCDigital:user', JSON.stringify(user))
      setData({ token: data.token, user })
      setCookie('token', data.token, 1)
    },
    [setData, data.token]
  )

  return (
    <Auth.Provider
      value={{
        token: data.token,
        recovery,
        sendRecovery,
        user: data.user,
        signIn,
        confirmMfa,
        signOut,
        signOutBecauseOfInactivity,
        updateUser,
        permissions,
        hasPermission,
        revalidatePermission,
        isManager: limitations.isManager,
        isCoordinator: limitations.isCoordinator,
        roles
      }}
    >
      {initialLoading && <LoadingPage />}
      {data.token && data.user && router.pathname !== '/404' ? (
        <MenuProvider>
          <LayoutSystem>{children}</LayoutSystem>
        </MenuProvider>
      ) : null}
      {!data.token && !data.user && router.pathname !== '/404' ? (
        <AuthPage />
      ) : null}
      {!data.token && data.user && router.pathname !== '/404' ? (
        <AuthPage signedOutBecauseOfInactivity />
      ) : null}
      {router.pathname === '/404' ? children : null}
    </Auth.Provider>
  )
}

export const useAuth = (): AuthContextData => {
  const context = useContext(Auth)

  if (!context) {
    throw new Error('The hook useAuth must be used within an AuthProvider')
  }

  return context
}
